import { useState, useCallback } from 'react';
import apiClient, { ApiError } from '../services/apiClient';
import type { ClientWithStats } from '../types';

export const useClientDetails = () => {
  const [clientDetails, setClientDetails] = useState<ClientWithStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load client details with statistics
  const loadClientDetails = useCallback(async (clientId: string): Promise<ClientWithStats | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const clientData = await apiClient.getClientDetails(clientId);
      setClientDetails(clientData);
      return clientData;
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      console.error('Failed to load client details:', apiError);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array to prevent infinite loops

  // Clear the current client details
  const clearClientDetails = useCallback(() => {
    setClientDetails(null);
    setError(null);
  }, []);

  return {
    clientDetails,
    isLoading,
    error,
    loadClientDetails,
    clearClientDetails,
    // Clear error manually
    clearError: () => setError(null)
  };
};

export default useClientDetails;
